import React, { useMemo } from "react";
import {
    Box, Typography, Paper, List, ListItem, ListItemText, ListItemAvatar, Avatar, Chip, Divider, Tooltip
} from "@mui/material";
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

type KycDocument = {
    id: number;
    status: string;
    type: string;
    comment: string;
};

type KycDocumentsProps = {
    documents: KycDocument[];
};

export default function KycDocuments({ documents }: KycDocumentsProps) {

    const getStatusPriority = (status: string): number => {
        const s = status.toUpperCase();
        if (s.includes('REJECTED')) return 1;
        if (s.includes('RECEIVED')) return 2;
        if (s === 'ACCEPTED') return 3;
        return 4;
    };

    const sortedDocuments = useMemo(() => {
        if (!documents) return [];
        const latestDocsMap = new Map<string, KycDocument>();

        documents.forEach(doc => {
            const existing = latestDocsMap.get(doc.type);
            if (!existing || doc.id > existing.id) {
                latestDocsMap.set(doc.type, doc);
            }
        });

        return Array.from(latestDocsMap.values())
            .sort((a, b) => {
                const prioA = getStatusPriority(a.status);
                const prioB = getStatusPriority(b.status);

                if (prioA !== prioB) {
                    return prioA - prioB;
                }

                return b.id - a.id;
            });
    }, [documents]);


    const getStatusConfig = (status: string) => {
        const s = status.toUpperCase();
        if (s === 'ACCEPTED') return { color: 'success' as const, icon: <CheckCircleOutlineIcon fontSize="inherit" /> };
        if (s.includes('REJECTED')) return { color: 'error' as const, icon: <ErrorOutlineIcon fontSize="inherit" /> };
        if (s.includes('RECEIVED')) return { color: 'warning' as const, icon: <HourglassEmptyIcon fontSize="inherit" /> };
        return { color: 'default' as const, icon: <DescriptionIcon fontSize="inherit" /> };
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: '#fafafa',
                height: '280px',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #e0e0e0'
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon fontSize="small" color="primary" /> KYC Documents ({sortedDocuments.length})
            </Typography>

            <Divider sx={{ mb: 1 }} />

            {sortedDocuments.length === 0
                ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">No documents uploaded.</Typography>
                    </Box>
                ) : (
                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        pr: 0.5,
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: '4px' }
                    }}>
                        <List dense disablePadding>
                            {sortedDocuments.map((doc, index) => {
                                const config = getStatusConfig(doc.status);
                                const isError = config.color === 'error';

                                return (
                                    <React.Fragment key={doc.id || index}>
                                        <ListItem sx={{ px: 0, py: 1 }}>
                                            <ListItemAvatar sx={{ minWidth: 40 }}>
                                                <Avatar sx={{
                                                    width: 32,
                                                    height: 32,
                                                    bgcolor: isError ? '#ffebee' : (doc.status === 'ACCEPTED' ? '#e8f5e9' : 'grey.200'),
                                                    color: isError ? 'error.main' : (doc.status === 'ACCEPTED' ? 'success.main' : 'grey.600')
                                                }}>
                                                    {isError ? <ErrorOutlineIcon sx={{ fontSize: 18 }} /> : <DescriptionIcon sx={{ fontSize: 18 }} />}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontWeight: 400,
                                                                color: 'text.secondary',
                                                                fontSize: '0.6rem',
                                                                fontFamily: 'monospace',
                                                                bgcolor: '#eeeeee',
                                                                px: 0.5,
                                                                borderRadius: 0.5
                                                            }}
                                                        >
                                                            #{doc.id}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2, color: isError ? 'error.main' : 'inherit' }}>
                                                            {doc.type.replace(/_/g, ' ')}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    doc.comment && (
                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                                                            {doc.comment}
                                                        </Typography>
                                                    )
                                                }
                                            />
                                            <Tooltip title={doc.status.replace(/_/g, ' ')} arrow>
                                                <Chip
                                                    icon={config.icon}
                                                    label={doc.status.split('_')[0]}
                                                    size="small"
                                                    color={config.color}
                                                    variant={doc.status === 'ACCEPTED' ? "outlined" : "filled"}
                                                    sx={{ fontSize: '0.6rem', height: 20, fontWeight: 700, minWidth: '75px' }}
                                                />
                                            </Tooltip>
                                        </ListItem>
                                        {index < sortedDocuments.length - 1 && <Divider variant="inset" component="li" sx={{ ml: 5, opacity: 0.5 }} />}
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    </Box>
                )}
        </Paper>
    );
}